<template>
  <div class="workstreams-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1>Workstreams</h1>
      <div class="header-actions">
        <button @click="refreshData" class="btn btn-secondary">
          <i class="icon-refresh">🔄</i> Refresh
        </button>
        <button v-if="isAdminOrAbove" @click="showCreateForm = true" class="btn btn-primary">
          <i class="icon-plus">➕</i> New Workstream
        </button>
      </div>
    </div>

    <!-- My Tasks Panel -->
    <div v-if="showMyTasks" class="my-tasks-panel">
      <div class="my-tasks-header">
        <h2>My All Tasks</h2>
        <div class="panel-actions">
          <button class="btn btn-secondary" @click="fetchMyTasks" :disabled="myTasksLoading">
            <i class="icon-refresh">🔄</i> Refresh
          </button>
          <button class="btn btn-secondary" @click="showMyTasks = false">Close</button>
        </div>
      </div>
      <div v-if="myTasksLoading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading your tasks...</p>
      </div>
      <div v-else-if="myTasks.length > 0" class="tasks-table">
        <div class="tasks-row tasks-header">
          <div>Task</div>
          <div>Workstream</div>
          <div>Assignees</div>
          <div>Due Date</div>
          <div>Status</div>
        </div>
<div 
          :class="['tasks-row', 'clickable', { 'is-hovered': hoveredTaskId === t.id }]" 
          v-for="t in myTasks" 
          :key="t.id"
          role="button"
          tabindex="0"
          @mouseenter="hoveredTaskId = t.id"
          @mouseleave="hoveredTaskId = null"
          @click="navigateToTaskWorkstream(t)"
          @keydown.enter="navigateToTaskWorkstream(t)"
          @keydown.space.prevent="navigateToTaskWorkstream(t)"
        >
          <div class="cell-title">{{ t.title || 'Untitled' }}</div>
          <div>{{ t.workstream?.name || '—' }}</div>
          <div>{{ formatAssigneesNames(t) }}</div>
          <div>{{ formatDateShort(t.due_date) }}</div>
          <div>{{ formatStatusLabel(t.status) }}</div>
        </div>
      </div>
      <div v-else class="empty-state">
        <div class="empty-icon">📋</div>
        <p>No tasks found.</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading workstreams...</p>
    </div>

    <!-- Workstreams Grid -->
    <div v-else-if="workstreams.length > 0" class="workstreams-grid">
      <div 
        v-for="ws in workstreams" 
        :key="ws.id"
        class="workstream-card"
        @click="$router.push({ name: 'workstream-detail', params: { id: ws.id } })"
      >
        <!-- Delete button (admins only) -->
        <button 
          v-if="isAdminOrAbove"
          class="card-delete"
          title="Delete workstream"
          aria-label="Delete workstream"
          @click.stop="deleteWorkstream(ws)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M9 3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h5a1 1 0 1 1 0 2h-1v14a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3V6H4a1 1 0 1 1 0-2h5V3zm2 1h2V4h-2V4zM7 6v14a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6H7zm3 3a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V10a1 1 0 0 1 1-1zm4 0a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V10a1 1 0 0 1 1-1z"/>
          </svg>
        </button>

        <div class="card-header">
          <div class="card-icon">📊</div>
          <div class="card-info">
            <h3>{{ ws.name || 'Untitled Workstream' }}</h3>
            <p class="card-description">{{ ws.description || 'No description' }}</p>
          </div>
        </div>
        <div class="card-stats">
          <div class="stat-item">
            <span class="stat-label">Members:</span>
            <span class="stat-value">{{ ws.member_count || 0 }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Tasks:</span>
            <span class="stat-value">{{ ws.task_count || 0 }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Goals:</span>
            <span class="stat-value">{{ ws.goal_count || 0 }}</span>
          </div>
        </div>
        <div v-if="getGoalsProgress(ws)" class="goals-progress">
          <div class="progress-summary">
            <span class="progress-label">Goals Progress:</span>
            <span class="progress-value">{{ getGoalsProgress(ws) }}</span>
          </div>
        </div>
        <div class="card-footer">
          <span class="updated-date">Last updated {{ formatDate(ws.updated_at || ws.created_at) }}</span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">📊</div>
      <h3>No Workstreams Yet</h3>
      <p>Create your first workstream to organize tasks and collaborate with your team.</p>
      <button @click="showCreateForm = true" class="btn btn-primary">
        <i class="icon-plus">➕</i> Create First Workstream
      </button>
    </div>

    <!-- Create Workstream Modal -->
    <div v-if="showCreateForm" class="modal-overlay" @click="showCreateForm = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Create New Workstream</h2>
          <button @click="showCreateForm = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="createWorkstream">
            <div class="form-group">
              <label for="name">Workstream Name *</label>
              <input 
                id="name"
                v-model="newWorkstream.name" 
                type="text" 
                placeholder="Enter workstream name"
                required
              />
            </div>
            <div class="form-group">
              <label for="description">Description *</label>
              <textarea 
                id="description"
                v-model="newWorkstream.description" 
                rows="3"
                placeholder="Enter workstream description (minimum 10 characters)"
                required
                minlength="10"
              ></textarea>
              <small class="form-hint">Minimum 10 characters, maximum 500</small>
            </div>
            <div class="form-group">
              <label for="purpose">Purpose *</label>
              <textarea 
                id="purpose"
                v-model="newWorkstream.purpose" 
                rows="3"
                placeholder="Enter the purpose of this workstream (minimum 10 characters)"
                required
                minlength="10"
              ></textarea>
              <small class="form-hint">Minimum 10 characters, maximum 300</small>
            </div>
            <div class="form-actions">
              <button type="button" @click="showCreateForm = false" class="btn btn-secondary">Cancel</button>
              <button type="submit" class="btn btn-primary" :disabled="!isFormValid">Create Workstream</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.workstreams-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.page-header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
}

.btn-secondary:hover {
  background: #f8f9fa;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #6c757d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.workstreams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.workstream-card {
  position: relative;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
}

.workstream-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

/* Delete button shown on hover */
.card-delete {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s, border-color 0.15s, transform 0.1s;
}

.workstream-card:hover .card-delete { opacity: 1; }

.card-delete svg { width: 16px; height: 16px; fill: #ef4444; }
.card-delete:hover { background: #fff5f5; border-color: #fecaca; }
.card-delete:hover svg { fill: #dc2626; }
.card-delete:active { transform: scale(0.96); }

.card-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.card-icon {
  font-size: 2.5rem;
}

.card-info h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
}

.card-description {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
}

.card-stats {
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  font-weight: 600;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.active {
  background: #d4edda;
  color: #155724;
}

.status-badge.inactive {
  background: #f8d7da;
  color: #721c24;
}

.goals-progress {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.progress-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
}

.progress-label {
  color: #6c757d;
  font-weight: 500;
}

.progress-value {
  color: #2c3e50;
  font-weight: 600;
}

.card-footer {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.updated-date {
  font-size: 0.85rem;
  color: #6c757d;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #6c757d;
  margin-bottom: 1.5rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.modal-close:hover {
  background: #f8f9fa;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  font-family: inherit;
  resize: vertical;
}

.form-hint {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* My Tasks Panel */
.my-tasks-panel {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}
.my-tasks-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.panel-actions { display: inline-flex; gap: 0.5rem; }
.tasks-table {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.5fr 1fr 1fr;
  gap: 0px;
}
 .tasks-row { display: contents; }
 .tasks-row > div {
  padding: 10px 8px;
  border-bottom: 1px solid #f1f3f5;
  color: #2c3e50;
  font-size: 0.95rem;
  transition: background-color 0.12s ease-in-out;
 }
 .tasks-row.clickable > div { cursor: pointer; }
 .tasks-row.is-hovered > div { background: #f8f9fa; }
 .tasks-header > div { font-weight: 600; color: #6c757d; }
 .cell-title { font-weight: 600; }
 
 </style>

<script>
import { onMounted, computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { tasksApi } from '@/services/api'

export default {
  name: 'WorkstreamsView',
  setup() {
    const store = useStore()
    const router = useRouter()
    const workstreams = computed(() => store.state.workstreams?.items || [])
    const loading = computed(() => store.state.workstreams?.loading || false)
    const isAdminOrAbove = computed(() => store.getters['auth/isAdminOrAbove'])
    const showCreateForm = ref(false)
    const newWorkstream = ref({
      name: '',
      description: '',
      purpose: ''
    })

    const refreshData = async () => {
      try {
        await store.dispatch('workstreams/fetchAll')
      } catch (e) {
        console.warn('Workstreams refresh failed', e?.response?.status || e?.message || e)
      }
    }

    const isFormValid = computed(() => {
      return newWorkstream.value.name.length >= 3 &&
             newWorkstream.value.description.length >= 10 &&
             newWorkstream.value.purpose.length >= 10
    })

    const createWorkstream = async () => {
      try {
        await store.dispatch('workstreams/create', {
          name: newWorkstream.value.name,
          description: newWorkstream.value.description,
          purpose: newWorkstream.value.purpose
        })
        showCreateForm.value = false
        newWorkstream.value = { name: '', description: '', purpose: '' }
        await refreshData()
      } catch (error) {
        console.error('Failed to create workstream:', error)
        alert('Failed to create workstream. Please check all required fields.')
      }
    }

    // My Tasks panel state and actions
    const showMyTasks = ref(false)
    const myTasks = ref([])
    const myTasksLoading = ref(false)
    const hoveredTaskId = ref(null)

    const fetchMyTasks = async () => {
      myTasksLoading.value = true
      try {
        const list = await tasksApi.listMine()
        myTasks.value = Array.isArray(list) ? list : (list?.tasks || [])
      } catch (e) {
        console.error('Failed to load my tasks', e)
        alert('Failed to load your tasks')
      } finally {
        myTasksLoading.value = false
      }
    }

    const navigateToTaskWorkstream = (t) => {
      if (!t) return
      const wsId = t.workstream_id || t.workstreamId || (t.workstream && (t.workstream.id || t.workstream.workstream_id))
      if (wsId) {
        // include task id so WorkstreamDetailView can auto-open its details panel
        const taskId = t.id || t.task_id || t.taskId
        router.push({ name: 'workstream-detail', params: { id: wsId }, query: taskId ? { task: taskId } : {} })
      } else {
        alert('No workstream found for this task')
      }
    }

    const openMyAsks = async () => {
      showMyTasks.value = true
      await fetchMyTasks()
      // keep query for potential deep-linking
      router.push({ name: 'workstreams', query: { view: 'my-asks' } })
    }

    const formatDate = (date) => {
      if (!date) return 'Unknown'
      const d = new Date(date)
      const now = new Date()
      const diffTime = Math.abs(now - d)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) return 'today'
      if (diffDays === 1) return 'yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
      return d.toLocaleDateString()
    }

    const formatDateShort = (date) => {
      if (!date) return '—'
      const d = new Date(date)
      if (isNaN(d.getTime())) return '—'
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return d.toLocaleDateString('en-US', options)
    }

    const formatAssignees = (arr) => {
      return Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : '—'
    }

    const formatAssigneesNames = (t) => {
      const details = Array.isArray(t?.assignees_details) ? t.assignees_details : []
      if (details.length > 0) {
        return details.map(d => d?.name || d?.email || d?.id).join(', ')
      }
      return formatAssignees(t?.assignees)
    }

    const formatStatusLabel = (status) => {
      const map = { in_progress: 'In Progress', done: 'Done', todo: 'Todo' }
      return map[status] || (status ? String(status) : '—')
    }

    const getGoalsProgress = (ws) => {
      if (!ws.goals || ws.goals.length === 0) return null
      const totalProgress = ws.goals.reduce((sum, goal) => sum + (goal.progress || 0), 0)
      const avgProgress = Math.round(totalProgress / ws.goals.length)
      const completedGoals = ws.goals.filter(g => g.progress === 100).length
      return `${completedGoals}/${ws.goals.length} complete (${avgProgress}% avg)`
    }

    const deleteWorkstream = async (ws) => {
      if (!ws?.id) return
      const name = ws.name || 'this workstream'
      const ok = window.confirm(`Delete ${name}? This cannot be undone.`)
      if (!ok) return
      try {
        await store.dispatch('workstreams/delete', ws.id)
      } catch (e) {
        console.error('Failed to delete workstream', e)
        alert('Failed to delete workstream')
      }
    }

    onMounted(refreshData)

    // Refresh when the logged-in user changes
    watch(() => store.state.auth.user?.id, () => {
      refreshData()
    })

    return { 
      workstreams, 
      loading,
      isAdminOrAbove,
      showCreateForm,
      newWorkstream,
      isFormValid,
      createWorkstream,
      refreshData,
      formatDate,
      formatDateShort,
      formatAssignees,
      formatAssigneesNames,
      formatStatusLabel,
      getGoalsProgress,
      deleteWorkstream,
      openMyAsks,
      showMyTasks,
      myTasks,
      myTasksLoading,
      hoveredTaskId,
      fetchMyTasks,
      navigateToTaskWorkstream
    }
  }
}
</script>
